import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { getManifest } from '@senses/games'

import { Alert, ConfirmDialog, Empty, Modal, Spinner } from '../components/ui.tsx'
import { api, type Assignment, type Program, type User } from '../lib/api.ts'
import { useAction, useAsync } from '../lib/hooks.ts'
import { formatNumber, formatRelative, plural } from '../lib/format.ts'
import { SessionTable } from '../components/SessionTable.tsx'

type Tab = 'plan' | 'results'

export function ClientDetailPage(): React.JSX.Element {
  const { id } = useParams()
  const userId = Number(id)
  const [tab, setTab] = useState<Tab>('plan')

  const client = useAsync(() => api.users.get(userId), [userId])
  const assignments = useAsync(() => api.assignments.list(userId), [userId])
  const summary = useAsync(() => api.sessions.summary(userId), [userId])
  const history = useAsync(() => api.sessions.list({ userId, limit: 200 }), [userId])

  const [assigning, setAssigning] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [editing, setEditing] = useState(false)

  if (client.loading) return <Spinner />
  if (client.error || !client.data) {
    return (
      <main className="page">
        <Alert>{client.error ?? 'Klient nenalezen'}</Alert>
      </main>
    )
  }

  const user = client.data.user

  return (
    <main className="page">
      <div className="page__header">
        <div>
          <p className="dim" style={{ fontSize: '0.85rem' }}>
            <Link to="/klienti">Klienti</Link> /
          </p>
          <h1>{user.name}</h1>
          <p>
            {user.email}
            {!user.active && <span className="badge badge--danger" style={{ marginLeft: '0.6rem' }}>deaktivovaný</span>}
          </p>
        </div>

        <div className="page__actions">
          <button type="button" className="btn" onClick={() => setEditing(true)}>
            Upravit údaje
          </button>
          <button type="button" className="btn" onClick={() => setResetting(true)}>
            Změnit heslo
          </button>
          <button type="button" className="btn btn--primary" onClick={() => setAssigning(true)}>
            Přiřadit program
          </button>
        </div>
      </div>

      {user.note && (
        <div className="alert alert--info" style={{ marginBottom: '1.5rem' }}>
          {user.note}
        </div>
      )}

      {summary.data && summary.data.summary.length > 0 && (
        <div className="stats" style={{ marginBottom: '2rem' }}>
          {summary.data.summary.map((item) => {
            const manifest = getManifest(item.gameId)
            return (
              <div className="stat" key={item.gameId}>
                <div className="stat__label">{manifest?.name ?? item.gameId}</div>
                <div className="stat__value">
                  {formatNumber(item.lastScore, manifest?.primaryMetric.precision ?? 2)}
                </div>
                <div className="stat__hint">
                  nejlépe {formatNumber(item.bestScore, manifest?.primaryMetric.precision ?? 2)} ·{' '}
                  {item.sessions} {plural(item.sessions, 'série', 'série', 'sérií')}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div className="topbar__nav" style={{ marginBottom: '1.5rem' }}>
        <TabButton active={tab === 'plan'} onClick={() => setTab('plan')} label="Tréninkový plán" />
        <TabButton active={tab === 'results'} onClick={() => setTab('results')} label="Výsledky" />
      </div>

      {tab === 'plan' && (
        <AssignmentList
          assignments={assignments.data?.assignments ?? []}
          loading={assignments.loading}
          error={assignments.error}
          onChanged={assignments.reload}
        />
      )}

      {tab === 'results' && (
        <>
          {(summary.data?.summary.length ?? 0) > 0 && (
            <div className="grid grid--wide" style={{ marginBottom: '2rem' }}>
              {(summary.data?.summary ?? []).map((item) => (
                <Link
                  key={item.gameId}
                  to={`/pokrok/${item.gameId}?userId=${userId}`}
                  className="card card--link"
                >
                  <h3>{getManifest(item.gameId)?.name ?? item.gameId}</h3>
                  <p className="muted" style={{ marginTop: '0.5rem' }}>
                    Graf vývoje po programech za {item.sessions}{' '}
                    {plural(item.sessions, 'sérii', 'série', 'sérií')}
                  </p>
                </Link>
              ))}
            </div>
          )}

          <div className="section__title">
            <h2>Odtrénované série</h2>
          </div>
          <SessionTable
            sessions={history.data?.sessions ?? []}
            loading={history.loading}
            error={history.error}
            emptyTitle="Klient zatím netrénoval"
            emptyHint="Jakmile dokončí první sérii, objeví se tady i s podrobným rozborem."
          />
        </>
      )}

      {assigning && (
        <AssignModal
          userId={userId}
          existing={assignments.data?.assignments ?? []}
          onClose={() => setAssigning(false)}
          onDone={() => {
            setAssigning(false)
            assignments.reload()
          }}
        />
      )}

      {resetting && (
        <PasswordModal userId={userId} name={user.name} onClose={() => setResetting(false)} />
      )}

      {editing && (
        <EditClientModal
          client={user}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false)
            client.reload()
          }}
        />
      )}
    </main>
  )
}

/** Úprava údajů klienta. Roli a přiřazení k trenérovi mění jen administrátor. */
function EditClientModal({
  client,
  onClose,
  onSaved,
}: {
  client: User
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const [name, setName] = useState(client.name)
  const [email, setEmail] = useState(client.email)
  const [note, setNote] = useState(client.note ?? '')
  const [active, setActive] = useState(client.active)

  const save = useAction(() =>
    api.users.update(client.id, {
      name,
      email,
      note: note.trim() ? note : null,
      active,
    }),
  )

  return (
    <Modal
      title="Údaje klienta"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Zrušit
          </button>
          <button type="submit" form="edit-client" className="btn btn--primary" disabled={save.pending}>
            {save.pending ? 'Ukládám…' : 'Uložit'}
          </button>
        </>
      }
    >
      <form
        id="edit-client"
        onSubmit={(event) => {
          event.preventDefault()
          void save.run().then((result) => result && onSaved())
        }}
      >
        {save.error && <Alert>{save.error}</Alert>}

        <label className="field">
          <span className="field__label">Jméno a příjmení</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={2}
            autoFocus
          />
        </label>

        <label className="field">
          <span className="field__label">E-mail</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <span className="field__help">
            Slouží zároveň jako přihlašovací jméno — po změně se klient přihlašuje novým.
          </span>
        </label>

        <label className="field">
          <span className="field__label">Poznámka</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Sport, cíl tréninku, zdravotní omezení…"
          />
          <span className="field__help">Vidí ji klient na svém přehledu.</span>
        </label>

        <div className="field field--inline">
          <input
            id="client-active"
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          <label htmlFor="client-active" className="field__label" style={{ cursor: 'pointer' }}>
            Účet je aktivní
          </label>
        </div>
        <p className="field__help">
          Deaktivací se klient okamžitě odhlásí a nemůže se přihlásit. Data i historie mu zůstanou.
        </p>
      </form>
    </Modal>
  )
}

function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean
  onClick: () => void
  label: string
}): React.JSX.Element {
  return (
    <button
      type="button"
      className={`topbar__link ${active ? 'topbar__link--active' : ''}`}
      onClick={onClick}
      style={{ border: 'none', background: active ? undefined : 'transparent', cursor: 'pointer' }}
    >
      {label}
    </button>
  )
}

/* ------------------------------------------------------------------ */

function AssignmentList({
  assignments,
  loading,
  error,
  onChanged,
}: {
  assignments: Assignment[]
  loading: boolean
  error: string | null
  onChanged: () => void
}): React.JSX.Element {
  if (loading) return <Spinner />
  if (error) return <Alert>{error}</Alert>

  if (assignments.length === 0) {
    return (
      <Empty title="Žádný přiřazený program">
        <p>Přiřaď klientovi program, aby měl co trénovat.</p>
      </Empty>
    )
  }

  return (
    <div className="grid grid--wide">
      {assignments.map((assignment) => (
        <AssignmentRow key={assignment.id} assignment={assignment} onChanged={onChanged} />
      ))}
    </div>
  )
}

function AssignmentRow({
  assignment,
  onChanged,
}: {
  assignment: Assignment
  onChanged: () => void
}): React.JSX.Element {
  const [removing, setRemoving] = useState(false)
  const manifest = assignment.program ? getManifest(assignment.program.gameId) : undefined

  const toggle = useAction(() => api.assignments.update(assignment.id, { active: !assignment.active }))
  const remove = useAction(() => api.assignments.remove(assignment.id))

  return (
    <div className="card" style={{ opacity: assignment.active ? 1 : 0.55 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>{manifest?.icon ?? '🎯'}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3>{assignment.program?.name ?? 'Smazaný program'}</h3>
          <p className="dim" style={{ fontSize: '0.85rem' }}>
            {manifest?.name ?? assignment.program?.gameId}
          </p>
        </div>
        {!assignment.active && <span className="badge">pozastaveno</span>}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
        <span className="badge">
          {assignment.completedSessions}
          {assignment.targetSessions ? ` / ${assignment.targetSessions}` : ''}{' '}
          {plural(assignment.targetSessions ?? assignment.completedSessions, 'série', 'série', 'sérií')}
        </span>
        {assignment.lastScore !== null && (
          <span className="badge badge--accent">
            {formatNumber(assignment.lastScore, manifest?.primaryMetric.precision ?? 2)}
          </span>
        )}
        <span className="dim" style={{ fontSize: '0.82rem', marginLeft: 'auto' }}>
          {formatRelative(assignment.lastPlayedAt)}
        </span>
      </div>

      {assignment.note && (
        <p className="muted" style={{ fontSize: '0.88rem', marginTop: '0.75rem' }}>
          {assignment.note}
        </p>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => void toggle.run().then(onChanged)}
          disabled={toggle.pending}
        >
          {assignment.active ? 'Pozastavit' : 'Obnovit'}
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setRemoving(true)}
          style={{ marginLeft: 'auto' }}
        >
          Odebrat
        </button>
      </div>

      {removing && (
        <ConfirmDialog
          title="Odebrat program"
          message={
            <>
              Opravdu odebrat <strong>{assignment.program?.name}</strong> z plánu? Odtrénované série
              zůstanou v historii.
            </>
          }
          confirmLabel="Odebrat"
          danger
          pending={remove.pending}
          onCancel={() => setRemoving(false)}
          onConfirm={() => void remove.run().then(onChanged)}
        />
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */

function AssignModal({
  userId,
  existing,
  onClose,
  onDone,
}: {
  userId: number
  existing: Assignment[]
  onClose: () => void
  onDone: () => void
}): React.JSX.Element {
  const programs = useAsync(() => api.programs.list(), [])
  const [programId, setProgramId] = useState<number | ''>('')
  const [targetSessions, setTargetSessions] = useState<string>('')
  const [note, setNote] = useState('')

  const assign = useAction(api.assignments.create)

  // Program, který klient už má, nabízet nemá smysl — API by ho odmítlo.
  const assigned = new Set(existing.map((a) => a.programId))
  const available: Program[] = (programs.data?.programs ?? []).filter((p) => !assigned.has(p.id))

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    if (programId === '') return
    const result = await assign.run({
      userId,
      programId,
      targetSessions: targetSessions ? Number(targetSessions) : null,
      note: note || null,
    })
    if (result) onDone()
  }

  return (
    <Modal
      title="Přiřadit program"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Zrušit
          </button>
          <button
            type="submit"
            form="assign-form"
            className="btn btn--primary"
            disabled={assign.pending || programId === ''}
          >
            {assign.pending ? 'Přiřazuji…' : 'Přiřadit'}
          </button>
        </>
      }
    >
      <form id="assign-form" onSubmit={(e) => void submit(e)}>
        {assign.error && <Alert>{assign.error}</Alert>}

        {programs.loading && <Spinner />}

        {programs.data && available.length === 0 && (
          <Alert kind="info">
            Klient už má přiřazené všechny existující programy. Založ nový v sekci Programy.
          </Alert>
        )}

        {available.length > 0 && (
          <>
            <label className="field">
              <span className="field__label">Program</span>
              <select
                value={programId}
                onChange={(e) => setProgramId(e.target.value ? Number(e.target.value) : '')}
                required
              >
                <option value="">— vyber program —</option>
                {available.map((program) => (
                  <option key={program.id} value={program.id}>
                    {program.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span className="field__label">Cílový počet sérií</span>
              <input
                type="number"
                min={1}
                max={1000}
                value={targetSessions}
                onChange={(e) => setTargetSessions(e.target.value)}
                placeholder="nepovinné"
              />
              <span className="field__help">
                Klient uvidí postup typu „3 / 10". Nech prázdné pro průběžný trénink bez cíle.
              </span>
            </label>

            <label className="field">
              <span className="field__label">Poznámka pro klienta</span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Např. dvakrát týdně, vždy před silovým tréninkem."
              />
            </label>
          </>
        )}
      </form>
    </Modal>
  )
}

function PasswordModal({
  userId,
  name,
  onClose,
}: {
  userId: number
  name: string
  onClose: () => void
}): React.JSX.Element {
  const [password, setPassword] = useState('')
  const [done, setDone] = useState(false)
  const reset = useAction(() => api.users.setPassword(userId, password))

  return (
    <Modal
      title="Změna hesla"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {done ? 'Zavřít' : 'Zrušit'}
          </button>
          {!done && (
            <button
              type="submit"
              form="pwd-form"
              className="btn btn--primary"
              disabled={reset.pending || password.length < 8}
            >
              {reset.pending ? 'Ukládám…' : 'Nastavit heslo'}
            </button>
          )}
        </>
      }
    >
      <form
        id="pwd-form"
        onSubmit={(e) => {
          e.preventDefault()
          void reset.run().then((result) => result && setDone(true))
        }}
      >
        {reset.error && <Alert>{reset.error}</Alert>}
        {done ? (
          <Alert kind="success">
            Heslo pro {name} bylo změněno. Klient byl odhlášen ze všech zařízení — předej mu nové heslo.
          </Alert>
        ) : (
          <label className="field">
            <span className="field__label">Nové heslo</span>
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
              autoFocus
            />
            <span className="field__help">Alespoň 8 znaků. Klient si ho může později změnit sám.</span>
          </label>
        )}
      </form>
    </Modal>
  )
}
